import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AddClosingTaskBody,
  ApproveDealerTaskTextBody,
  CaseStage,
  ClosingBlocker,
  ClosingStatus,
  ClosingTaskNoteBody,
  ClosingTaskOwner,
  ClosingTaskStatus,
  ClosingTaskView,
  ClosingView,
  CompleteClosingTaskBody,
  EnterClosingBody,
  FeeEntryType,
  FeeLedgerResponse,
  FundingEventKind,
  FundingResponse,
  PayeeCategory,
  RecordFeeEntryBody,
  RecordFeeEntryResponse,
  RecordFundingEventBody,
  RecordFundingEventResponse,
  TexClosingTaskView,
} from '@tex/finance-shared'
import { CLOSING_REASONS } from '@tex/finance-shared'
import { ApiError, apiGet, apiSend } from './api'
import { caseKeys } from './cases'
import { closingKeys, offerKeys } from './offers'

const IDEMPOTENCY_HEADER = 'Idempotency-Key'
const base = (caseId: string) => `/api/v1/cases/${caseId}`

export function useClosing(caseId: string, enabled = true) {
  return useQuery({ queryKey: closingKeys.closing(caseId), queryFn: () => apiGet<ClosingView>(`${base(caseId)}/closing`), enabled })
}

export function useFunding(caseId: string, enabled = true) {
  return useQuery({ queryKey: closingKeys.funding(caseId), queryFn: () => apiGet<FundingResponse>(`${base(caseId)}/funding-events`), enabled })
}

/** Ops only on the server (`fee_ledger.read`); callers gate `enabled` on the role so reviewers never see a 403. */
export function useFees(caseId: string, enabled = true) {
  return useQuery({ queryKey: closingKeys.fees(caseId), queryFn: () => apiGet<FeeLedgerResponse>(`${base(caseId)}/fees`), enabled })
}

/**
 * Every closing-side command refreshes the closing view; stage-changing ones (`enterClosing`,
 * `reevaluateClosing`, partner funding) also refresh the case, offers and the work queue.
 * A `version_conflict` means the cached `case_version` is stale, so the view is refetched — and, for
 * stage-changing commands, so are the case header, offers and queue, since the race usually was a stage change.
 */
function useClosingMutation<TVars, TResult>(caseId: string, fn: (vars: TVars) => Promise<TResult>, stageChanging: boolean) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: closingKeys.closing(caseId) }),
        qc.invalidateQueries({ queryKey: closingKeys.funding(caseId) }),
        qc.invalidateQueries({ queryKey: closingKeys.fees(caseId) }),
        ...(stageChanging
          ? [
              qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) }),
              qc.invalidateQueries({ queryKey: offerKeys.offers(caseId) }),
              qc.invalidateQueries({ queryKey: ['work-queue'] }),
            ]
          : []),
      ]),
    onError: (err) => {
      if (!(err instanceof ApiError) || (err.code !== 'version_conflict' && err.code !== 'task_changed')) return
      return Promise.all([
        qc.invalidateQueries({ queryKey: closingKeys.closing(caseId) }),
        ...(stageChanging
          ? [
              qc.invalidateQueries({ queryKey: caseKeys.detail(caseId) }),
              qc.invalidateQueries({ queryKey: offerKeys.offers(caseId) }),
              qc.invalidateQueries({ queryKey: ['work-queue'] }),
            ]
          : []),
      ])
    },
  })
}

export function useEnterClosing(caseId: string) {
  return useClosingMutation(caseId, (body: EnterClosingBody) => apiSend<ClosingView>('POST', `${base(caseId)}/closing`, body), true)
}

export function useReevaluateClosing(caseId: string) {
  return useClosingMutation(
    caseId,
    (body: { expected_version: number; note: string }) => apiSend<ClosingView>('POST', `${base(caseId)}/closing/reevaluate`, body),
    true,
  )
}

export function useAddClosingTask(caseId: string) {
  return useClosingMutation(caseId, (body: AddClosingTaskBody) => apiSend<ClosingTaskView>('POST', `${base(caseId)}/closing/tasks`, body), false)
}

export function useCompleteClosingTask(caseId: string) {
  return useClosingMutation(
    caseId,
    ({ taskId, body }: { taskId: string; body: CompleteClosingTaskBody }) =>
      apiSend<ClosingTaskView>('POST', `${base(caseId)}/closing/tasks/${taskId}/complete`, body),
    false,
  )
}

export function useApproveDealerTaskText(caseId: string) {
  return useClosingMutation(
    caseId,
    ({ taskId, body }: { taskId: string; body: ApproveDealerTaskTextBody }) =>
      apiSend<TexClosingTaskView>('POST', `${base(caseId)}/closing/tasks/${taskId}/approve-dealer-text`, body),
    false,
  )
}

export type ClosingTaskVerb = 'waive' | 'block' | 'reopen'

export function useClosingTaskCommand(caseId: string) {
  return useClosingMutation(
    caseId,
    ({ taskId, verb, body }: { taskId: string; verb: ClosingTaskVerb; body: ClosingTaskNoteBody }) =>
      apiSend<ClosingTaskView>('POST', `${base(caseId)}/closing/tasks/${taskId}/${verb}`, body),
    false,
  )
}

/** `idempotencyKey` is minted once per form and reused on retry so a double click records one event. */
export function useRecordFunding(caseId: string) {
  return useClosingMutation(
    caseId,
    ({ body, idempotencyKey }: { body: RecordFundingEventBody; idempotencyKey: string }) =>
      apiSend<RecordFundingEventResponse>('POST', `${base(caseId)}/funding-events`, body, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
    true,
  )
}

export function useRecordFee(caseId: string) {
  return useClosingMutation(
    caseId,
    ({ body, idempotencyKey }: { body: RecordFeeEntryBody; idempotencyKey: string }) =>
      apiSend<RecordFeeEntryResponse>('POST', `${base(caseId)}/fees`, body, { [IDEMPOTENCY_HEADER]: idempotencyKey }),
    false,
  )
}

// ── labels ───────────────────────────────────────────────────────────────────

export const CLOSING_STATUS_LABELS: Record<ClosingStatus, string> = {
  not_started: 'Not started',
  ready_to_close: 'Ready to enter closing',
  closing: 'In closing',
  invalidated: 'Closing invalidated',
  funded: 'Funded',
  closed: 'Closed',
}

export const TASK_OWNER_LABELS: Record<ClosingTaskOwner, string> = {
  borrower: 'Buyer',
  dealer: 'Dealer',
  tex: 'Tex',
  partner: 'Partner',
}

export const TASK_STATUS_LABELS: Record<ClosingTaskStatus, string> = {
  open: 'Open',
  blocked: 'Blocked',
  complete: 'Complete',
  waived: 'Waived',
}

export const FUNDING_KIND_LABELS: Record<FundingEventKind, string> = {
  partner_confirmed_funding: 'Partner confirmed funding',
  dealer_confirmed_receipt: 'Seller confirmed receipt',
  correction: 'Correction',
  reversal: 'Reversal',
}

export const PAYEE_LABELS: Record<PayeeCategory, string> = {
  seller: 'Seller (dealer)',
  borrower: 'Buyer',
  other: 'Other (say who in the note)',
}

export const FEE_ENTRY_LABELS: Record<FeeEntryType, string> = {
  expected_fee: 'Expected fee',
  fee_collected: 'Fee collected',
  correction: 'Correction',
}

/** Plain-language blocker, in the words the shared package already commits to where it has them. */
export function blockerText(b: ClosingBlocker): string {
  switch (b.kind) {
    case 'invalid_stage':
      return `The case is in ${b.stage.replace(/_/g, ' ')}, not offer review or closing.`
    case 'closing_tasks_open':
      return `${b.task_ids.length} closing ${b.task_ids.length === 1 ? 'task is' : 'tasks are'} still open or blocked.`
    case 'funding_not_confirmed':
      return 'The partner has not confirmed funding with evidence.'
    default:
      return CLOSING_REASONS[b.kind]
  }
}

/** Which task commands the server will accept for a task in this status. */
export function taskActions(status: ClosingTaskStatus): { complete: boolean; waive: boolean; block: boolean; reopen: boolean } {
  return {
    complete: status === 'open' || status === 'blocked',
    waive: status === 'open' || status === 'blocked',
    block: status === 'open',
    reopen: status !== 'open',
  }
}

/** Stages at which a closing card says something: from closing onwards. */
export const CLOSING_STAGES: readonly CaseStage[] = ['closing', 'funded', 'closed']
